import { HEADER_GROUP_ROW_HEIGHT } from '@/modules/balanceSheet/BalanceSheetTable/BalanceSheetTable.layout';
import { FlatRow } from '@/modules/balanceSheet/BalanceSheetTable/BalanceSheetTable.utils';
import { BalanceSheetRowType } from '@/types/schema';

// All heights at 'default' density (presentation mode).

/**
 * Usable slide body height in pixels. Same 795px base used by the Estate
 * Waterfall Assumptions slide (see EstateWaterfallAssumptionsSlide.constants.ts).
 */
const SLIDE_BODY_HEIGHT_PX = 795;

/** Column header row has the same height as the group header (42px) */
const COLUMN_HEADER_ROW_HEIGHT_PX = HEADER_GROUP_ROW_HEIGHT;

/** 12px pad + 16px line + 12px pad + 1px border */
const DATA_ROW_HEIGHT_PX = 41;

/** 12px pad + 16px line + 12px pad + 2px border */
const FOOTER_ROW_HEIGHT_PX = 42;

/** Secondary text adds a 16px line and a 4px gap. */
const DESCRIPTION_HEIGHT_PX = 20;
const DETAIL_LINE_HEIGHT_PX = 16;

const CONTINUED_MSG_HEIGHT_PX = 24;

export interface BalanceSheetPage {
  rows: FlatRow[];
  isLastPage: boolean;
}

export interface PaginateBalanceSheetOptions {
  summaryMetricsHeightPx?: number;
  showColumnGroups?: boolean;
  detailsInColumn?: boolean;
}

function isFooterRow(flatRow: FlatRow): boolean {
  const variant = flatRow.row.displayVariant ?? flatRow.row.variant;
  return variant === BalanceSheetRowType.NetWorth || variant === 'SUBTOTALS';
}

function getRowHeight(flatRow: FlatRow, detailsInColumn = false): number {
  const variant = flatRow.row.displayVariant ?? flatRow.row.variant;
  const description = flatRow.row.name.lineTwo;
  let nameHeight = description ? DESCRIPTION_HEIGHT_PX : 0;
  if (detailsInColumn) {
    nameHeight =
      Math.max(0, (description?.split('\n').length ?? 1) - 1) *
      DETAIL_LINE_HEIGHT_PX;
  }
  const valueHeight = Object.values(flatRow.row.values).some(
    (value) => value.description
  )
    ? DESCRIPTION_HEIGHT_PX
    : 0;
  const descriptionHeight = Math.max(nameHeight, valueHeight);
  switch (variant) {
    // Subtotals and NetWorth have a 2px bottom border
    case BalanceSheetRowType.NetWorth:
    case 'SUBTOTALS':
      return FOOTER_ROW_HEIGHT_PX + descriptionHeight;
    default:
      return DATA_ROW_HEIGHT_PX + descriptionHeight;
  }
}

function isCategoryRow(flatRow: FlatRow): boolean {
  return (
    flatRow.row.variant === BalanceSheetRowType.Category && !flatRow.isChild
  );
}

/**
 * Splits a flat list of balance sheet rows into pages that fit within a
 * presentation slide's available height. Headers repeat on every page.
 *
 * Footer rows (Subtotals + NetWorth) are always placed on the last page.
 * A category header is never left as the last row on a page (anti-orphan).
 */
export function paginateBalanceSheetRows(
  flatRows: FlatRow[],
  opts?: PaginateBalanceSheetOptions
): BalanceSheetPage[] {
  if (flatRows.length === 0) {
    return [{ rows: [], isLastPage: true }];
  }

  const summaryMetricsHeight = opts?.summaryMetricsHeightPx ?? 0;
  const measureRowHeight = (row: FlatRow) =>
    getRowHeight(row, opts?.detailsInColumn);
  const headerOverhead =
    (opts?.showColumnGroups === false ? 0 : HEADER_GROUP_ROW_HEIGHT) +
    COLUMN_HEADER_ROW_HEIGHT_PX;

  const bodyRows: FlatRow[] = [];
  const footerRows: FlatRow[] = [];
  for (const row of flatRows) {
    if (isFooterRow(row)) {
      footerRows.push(row);
    } else {
      bodyRows.push(row);
    }
  }

  const footerHeight = footerRows.reduce(
    (sum, row) => sum + measureRowHeight(row),
    0
  );

  const firstPageBudget =
    SLIDE_BODY_HEIGHT_PX - headerOverhead - summaryMetricsHeight;

  const totalBodyHeight = bodyRows.reduce(
    (sum, row) => sum + measureRowHeight(row),
    0
  );

  // Everything fits on a single page
  if (totalBodyHeight + footerHeight <= firstPageBudget) {
    return [{ rows: [...bodyRows, ...footerRows], isLastPage: true }];
  }

  // Multi-page pagination
  const pages: BalanceSheetPage[] = [];
  let currentPageRows: FlatRow[] = [];
  let remainingHeight = firstPageBudget - CONTINUED_MSG_HEIGHT_PX;

  const fullPageBudget =
    SLIDE_BODY_HEIGHT_PX - headerOverhead - CONTINUED_MSG_HEIGHT_PX;

  for (let i = 0; i < bodyRows.length; i++) {
    const row = bodyRows[i]!;
    const rowHeight = measureRowHeight(row);

    if (remainingHeight >= rowHeight) {
      // Anti-orphan: don't leave a category header as the last row on a
      // page. Skipped when the category is the last body row — the
      // footer rows get appended to its page, so it isn't orphaned.
      if (isCategoryRow(row) && currentPageRows.length > 0) {
        const nextRow = bodyRows[i + 1];
        if (
          nextRow &&
          remainingHeight - rowHeight < measureRowHeight(nextRow)
        ) {
          pages.push({ rows: currentPageRows, isLastPage: false });
          currentPageRows = [row];
          remainingHeight = fullPageBudget - rowHeight;
          continue;
        }
      }

      currentPageRows.push(row);
      remainingHeight -= rowHeight;
    } else {
      pages.push({ rows: currentPageRows, isLastPage: false });
      currentPageRows = [row];
      remainingHeight = fullPageBudget - rowHeight;
    }
  }

  // Append footer rows — reclaim continued-msg space since this becomes the last page
  if (remainingHeight + CONTINUED_MSG_HEIGHT_PX >= footerHeight) {
    currentPageRows.push(...footerRows);
    pages.push({ rows: currentPageRows, isLastPage: true });
  } else {
    pages.push({ rows: currentPageRows, isLastPage: false });
    pages.push({ rows: [...footerRows], isLastPage: true });
  }

  return pages;
}
